import clsx from "clsx";
import React from "react";
import { Tree as HeadlessTree } from "react-aria-components";

import styles from "./styles.module.css";
import { TreeNodeRow } from "./TreeNodeRow";
import type { TreeNodeItem, TreeProps } from "./types";

/**
 * WDS Tree — a display tree built on react-aria-components 1.7.1's STABLE
 * (GA, un-prefixed) Tree / TreeItem / TreeItemContent exports.
 *
 * A11Y — DELIBERATE, DOCUMENTED, NOT A BUG: RAC's GA Tree renders the ARIA
 * *treegrid* pattern (role="treegrid" → row → gridcell), not role="tree".
 * Screen readers announce it as a grid of rows with expand/collapse; tests
 * and consumers must target the treegrid roles.
 *
 * BANNED: `UNSTABLE_TreeLoadingIndicator`. It is an UNSTABLE_-prefixed RAC
 * export with no API guarantee — do not import it here or in consumers.
 * Loading UX is the consumer's job (the widget layer swaps in a Skeleton on
 * first load per the M3 async contract).
 *
 * v1 scope notes (mirrored in the widget doc):
 * - NO virtualization: every expanded row is real DOM. Practical ceiling is
 *   the tree-family clamp (5000 nodes / depth 10) with a few hundred
 *   *expanded* rows as the comfortable envelope.
 * - NO lazy/async children, NO multi-select/checkboxes (TreeSelect's job),
 *   NO drag-and-drop.
 *
 * Selection defaults to `single`; expansion/selection are RAC-controlled or
 * -uncontrolled via the usual (default)expandedKeys/(default)selectedKeys
 * props. Empty content renders through RAC's `renderEmptyState` passthrough
 * — this primitive hard-codes no copy (M3 contract).
 */
const EMPTY_ITEMS: TreeNodeItem[] = [];

export const Tree = (props: TreeProps) => {
  const {
    className,
    items = EMPTY_ITEMS,
    selectionMode = "single",
    ...rest
  } = props;
  const hasLabelling =
    Boolean(props["aria-label"]) || Boolean(props["aria-labelledby"]);

  return (
    <HeadlessTree
      aria-label={props["aria-label"] ?? (hasLabelling ? undefined : "Tree")}
      className={clsx(styles.tree, className)}
      items={items}
      selectionMode={selectionMode}
      {...rest}
    >
      {(node: TreeNodeItem) => <TreeNodeRow node={node} />}
    </HeadlessTree>
  );
};
